import { type FC, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { IN, type MeasurementUnit } from 'dibs-intl/exports/units';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { trackEvent } from 'dibs-tracking';

import Ruler from 'dibs-icons/exports/legacy/Ruler';

import { type ProductDetails_item$key } from './__generated__/ProductDetails_item.graphql';
import { type ProductDetails_user$key } from './__generated__/ProductDetails_user.graphql';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

const messages = {
    sponsored: <FormattedMessage id="dc.buyerProductTile.sponsored" defaultMessage="Sponsored" />,
    offeredBy: (sellerName: string): ReactElement => (
        <FormattedMessage
            id="dc.buyerProductTile.offeredBy"
            defaultMessage={`Offered By {sellerName}`}
            values={{ sellerName }}
        />
    ),
};

const itemFragment = graphql`
    fragment ProductDetails_item on Item {
        serviceId
        measurement {
            display(
                addLabel: true
                separator: " "
                fields: [height, width, depth, diameter, length]
            ) {
                unit
                value
            }
            size {
                value
            }
        }
        seller @include(if: $showSeller) {
            sellerProfile {
                company
            }
        }
    }
`;

const userFragment = graphql`
    fragment ProductDetails_user on User {
        canShowSellerName
    }
`;

type Props = {
    item: ProductDetails_item$key | null | undefined;
    user: ProductDetails_user$key | null | undefined;
    afterDisplay: boolean;
    isSponsored?: boolean;
    measurementUnit?: MeasurementUnit;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
};

export const ProductDetails: FC<Props> = ({
    item: itemRef,
    user: userRef,
    measurementUnit = IN,
    afterDisplay,
    isSponsored,
    showBrowseSimilar,
    showMeasurements,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const itemId = item?.serviceId || '';
    const { display, size } = item?.measurement || {};
    const { canShowSellerName } = user || {};
    const sellerName = item?.seller?.sellerProfile?.company;
    const showSellerName = afterDisplay && canShowSellerName && sellerName;

    const dimensionValues =
        size?.value ||
        (display || []).find(dimension => dimension?.unit === measurementUnit)?.value ||
        '';
    const canShowDimensions = dimensionValues && showMeasurements;

    return (
        <p
            className={classnames(
                dibsCss.textBodyXSmallNormal,
                dibsCss.wFull,
                dibsCss.h18px,
                dibsCss.mtXxsmall,
                dibsCss.flex,
                dibsCss.flexRow,
                dibsCss.itemsCenter
            )}
        >
            <span
                className={classnames(
                    dibsCss.textBodyXSmallNormal,
                    dibsCss.textSatan,
                    dibsCss.wFull,
                    dibsCss.flex,
                    dibsCss.minW0,
                    {
                        [styles.itemDetails]: canShowDimensions, //hide element if dimensions are not shown
                    }
                )}
            >
                {showSellerName && (
                    <span
                        className={classnames(dibsCss.truncate, dibsCss.flexShrink, {
                            [dibsCss.borderRSolid]: isSponsored,
                            [dibsCss.borderRDolphin]: isSponsored,
                            [dibsCss.borderR]: isSponsored,
                            [dibsCss.mrXsmall]: isSponsored,
                            [dibsCss.prXsmall]: isSponsored,
                        })}
                    >
                        {messages.offeredBy(sellerName)}
                    </span>
                )}
                {isSponsored && messages.sponsored}
            </span>
            {canShowDimensions ? (
                <span
                    className={classnames(
                        styles.browseSimilar,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.truncate
                    )}
                >
                    <Ruler className={classnames(dibsCss.h14px, dibsCss.mrXxsmall)} />
                    {dimensionValues}
                </span>
            ) : null}
            {showBrowseSimilar && (
                <FormattedMessage
                    id="dc.buyerProductTile.browseSimilar"
                    defaultMessage="Browse Similar"
                >
                    {msg => (
                        <SeoLink
                            className={classnames(
                                dibsCss.textBodyXSmallHeavy,
                                dibsCss.textNoir,
                                dibsCss.flexNowrap,
                                dibsCss.shrink0,
                                dibsCss.mlAuto,
                                dibsCss.plSmall,
                                styles.browseSimilar
                            )}
                            dataTn="browse-similar"
                            linkData={{
                                path: `/similar/?item=${itemId}`,
                                isLinkable: false,
                            }}
                            onClick={() =>
                                trackEvent({
                                    category: 'product',
                                    action: 'item tile browse similar click',
                                })
                            }
                        >
                            {msg}
                        </SeoLink>
                    )}
                </FormattedMessage>
            )}
        </p>
    );
};
